import {
  Book,
  Check2Circle,
  FileEarmarkPlus,
  House,
  Key
} from 'react-bootstrap-icons';
import { HouseIcon } from '@ghq-abi/design-system-icons';

import { EntitiesEnum } from '~/shared/auth/permissions';
import { Icon } from '~/shared/components';
import { PagesPathsKeys } from '~/shared/constants/pages';
import { REPORT_ALLOWED_PROFILES } from '~/shared/constants/permissions';
import type { RequestProduct } from '~/shared/constants/requestProducts';

const ICON_COLOR = '#191F2E';
const ICON_SIZE = 20;

export interface SubRouteConfig {
  key: string;
  title: string;
  translationKey?: string;
  literalTitle?: boolean;
  path: string;
  entity: EntitiesEnum;
  flag: PagesPathsKeys;
  icon?: React.ReactElement;
  subRoutes?: {
    app?: SubRouteConfig[];
    eib: SubRouteConfig[];
  };
}

export interface RouteConfig {
  key: {
    app: string | undefined;
    eib: string | undefined;
  };
  title: { app: string; eib: string };
  path: {
    app: string | undefined;
    eib: string | undefined;
  };
  entity: { app: EntitiesEnum; eib: EntitiesEnum };
  flag: PagesPathsKeys;
  icons: {
    app: React.ReactElement;
    eib: React.ReactElement;
  };
  profileRestriction?: {
    app?: import('~/shared/types/profile').UserProfileType[];
    eib?: import('~/shared/types/profile').UserProfileType[];
  };
  subRoutes?: {
    app?: SubRouteConfig[];
    eib?: SubRouteConfig[];
  };
}

function createMenuRoute(
  key: { app: string; eib: string },
  title: { app: string; eib: string },
  path: { app: string; eib: string },
  entity: { app: EntitiesEnum; eib: EntitiesEnum },
  flag: PagesPathsKeys,
  icons: { app: React.ReactElement; eib: React.ReactElement },
  options?: Pick<RouteConfig, 'profileRestriction' | 'subRoutes'>
): RouteConfig {
  return {
    key,
    title,
    path,
    entity,
    flag,
    icons,
    ...options
  };
}

function eibRequestSubRoute(
  key: string,
  title: string,
  path: string,
  icon: React.ReactElement
): SubRouteConfig {
  return {
    key,
    title,
    path,
    entity: EntitiesEnum.Requests,
    flag: 'newRequest',
    icon
  };
}

export function buildRequestProductSubRoutes(
  products: Pick<RequestProduct, 'key' | 'title'>[]
): SubRouteConfig[] {
  return products.flatMap(product => {
    const productRoute: SubRouteConfig = {
      key: product.key,
      title: product.title,
      literalTitle: true,
      path: `new-request?product=${product.key}`,
      entity: EntitiesEnum.Requests,
      flag: 'newRequest' as PagesPathsKeys
    };

    if (product.key !== 'workday') {
      return [productRoute];
    }

    return [
      productRoute,
      {
        key: 'workdayMassiveRequest',
        title: 'Workday Massive Request',
        translationKey: 'menu.newRequest.subRoutes.workdayMassiveRequest',
        path: 'massive-request',
        entity: EntitiesEnum.Requests,
        flag: 'newRequest' as PagesPathsKeys
      }
    ];
  });
}

const EIB_COMPENSATION_ICON = (
  <Icon.CashCoin color={ICON_COLOR} size={ICON_SIZE} />
);

export const MENU_ROUTES_CONFIG: RouteConfig[] = [
  createMenuRoute(
    { app: 'home', eib: 'home' },
    { app: 'Home', eib: 'Home' },
    { app: 'home', eib: 'home' },
    { app: EntitiesEnum.Home, eib: EntitiesEnum.Home },
    'home',
    {
      app: <House color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <HouseIcon />
    }
  ),
  createMenuRoute(
    { app: 'newRequest', eib: 'newRequest' },
    { app: 'New Request', eib: 'New Request' },
    { app: 'new-request', eib: 'new-request' },
    { app: EntitiesEnum.Requests, eib: EntitiesEnum.Requests },
    'newRequest',
    {
      app: <FileEarmarkPlus color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <Icon.SubmitDocument color={ICON_COLOR} size={ICON_SIZE} />
    },
    {
      subRoutes: {
        eib: [
          {
            key: 'compensationChanges',
            title: 'Compensation Changes',
            path: 'new-request-salary-increase',
            entity: EntitiesEnum.Requests,
            flag: 'newRequest',
            subRoutes: {
              eib: [
                eibRequestSubRoute(
                  'salaryIncrease',
                  'Salary Increase',
                  'new-request-salary-increase',
                  EIB_COMPENSATION_ICON
                ),
                eibRequestSubRoute(
                  'assignPlan',
                  'Assign Plan',
                  'new-request-assign-plan',
                  EIB_COMPENSATION_ICON
                ),
                eibRequestSubRoute(
                  'removePlan',
                  'Remove Plan',
                  'new-request-remove-plan',
                  EIB_COMPENSATION_ICON
                ),
                eibRequestSubRoute(
                  'defaultCompensationChange',
                  'Default Compensation Change',
                  'new-request-default-compensation-change',
                  EIB_COMPENSATION_ICON
                )
              ]
            },
            icon: <Icon.MoneyBag color={ICON_COLOR} size={ICON_SIZE} />
          },
          eibRequestSubRoute(
            'statusPosition',
            'Status Position',
            'new-request-status-position',
            <Icon.Badge color={ICON_COLOR} size={ICON_SIZE} />
          )
        ]
      }
    }
  ),
  createMenuRoute(
    { app: 'myAccess', eib: 'requests' },
    { app: 'My Access', eib: 'My Requests' },
    { app: 'my-access', eib: 'requests' },
    { app: EntitiesEnum.MyAccess, eib: EntitiesEnum.Requests },
    'myAccess',
    {
      app: <Key color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <Icon.Table color={ICON_COLOR} size={ICON_SIZE} />
    }
  ),
  createMenuRoute(
    { app: 'toApprove', eib: 'toApprove' },
    { app: 'To Approve', eib: 'Approvals' },
    { app: 'to-approve', eib: 'to-approve' },
    { app: EntitiesEnum.ToApprove, eib: EntitiesEnum.ToApprove },
    'toApprove',
    {
      app: <Check2Circle color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <Icon.ApproveAndReject color={ICON_COLOR} size={ICON_SIZE} />
    },
    { profileRestriction: { app: undefined, eib: ['Zone Spoc'] } }
  ),
  createMenuRoute(
    { app: 'report', eib: 'report' },
    { app: 'Report', eib: 'Report' },
    { app: 'report', eib: 'report' },
    { app: EntitiesEnum.Report, eib: EntitiesEnum.Report },
    'report',
    {
      app: <Icon.Reports color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <Icon.Reports color={ICON_COLOR} size={ICON_SIZE} />
    },
    {
      profileRestriction: {
        app: REPORT_ALLOWED_PROFILES,
        eib: REPORT_ALLOWED_PROFILES
      }
    }
  ),
  createMenuRoute(
    { app: 'guide', eib: 'guide' },
    { app: 'Guide', eib: 'Guide' },
    { app: 'guide', eib: 'guide' },
    { app: EntitiesEnum.Guide, eib: EntitiesEnum.Guide },
    'guide',
    {
      app: <Book color={ICON_COLOR} size={ICON_SIZE} />,
      eib: <Icon.InfoCircleFilled color={ICON_COLOR} size={ICON_SIZE} />
    }
  )
];

export function getSubRouteTitle(
  subRoute: SubRouteConfig,
  parentKey: string,
  t: (key: string, defaultValue: string) => string
): string {
  if (subRoute.literalTitle) {
    return subRoute.title;
  }
  if (subRoute.translationKey) {
    return t(subRoute.translationKey, subRoute.title);
  }
  return t(`menu.${parentKey}.subRoutes.${subRoute.key}`, subRoute.title);
}
